/**
 * What a blue screen says.
 *
 * XP's stop screen is three fixed paragraphs of advice wrapped around two
 * pieces of real information: the bugcheck — a code and the symbolic name the
 * kernel knows it by — and, when the fault could be pinned on one, the driver
 * that caused it. Everything here is drawn from that second part; the prose in
 * `Bsod.tsx` is verbatim and never changes, exactly as it never did.
 */

export interface Bugcheck {
  /** `0x0000000A`, as printed after `*** STOP:`. */
  code: string;
  /** `IRQL_NOT_LESS_OR_EQUAL` — the name on the line above the advice. */
  name: string;
  /** The four bugcheck parameters, already formatted. */
  args: [string, string, string, string];
  /**
   * The blamed module and its addresses, or null. A stop with no module named
   * is the more frightening kind, and roughly a third of these are.
   */
  driver: {
    name: string;
    /** Where it faulted. */
    address: string;
    /** Where it was loaded. */
    base: string;
    /** The link timestamp, printed raw — XP never made it a date for you. */
    dateStamp: string;
  } | null;
}

/**
 * Real XP bugchecks, with the drivers each one plausibly came from. The
 * pairing matters: `NTFS_FILE_SYSTEM` blamed on `nv4_disp.dll` would read as
 * nonsense to anybody who ever had to look one of these up.
 */
const CHECKS: { code: number; name: string; drivers: string[] }[] = [
  { code: 0x0a, name: 'IRQL_NOT_LESS_OR_EQUAL', drivers: ['ntoskrnl.exe', 'win32k.sys'] },
  { code: 0x1e, name: 'KMODE_EXCEPTION_NOT_HANDLED', drivers: ['ntoskrnl.exe'] },
  { code: 0x24, name: 'NTFS_FILE_SYSTEM', drivers: ['ntfs.sys', 'atapi.sys'] },
  { code: 0x2e, name: 'DATA_BUS_ERROR', drivers: [] },
  { code: 0x50, name: 'PAGE_FAULT_IN_NONPAGED_AREA', drivers: ['win32k.sys', 'tcpip.sys'] },
  { code: 0x7b, name: 'INACCESSIBLE_BOOT_DEVICE', drivers: [] },
  { code: 0x7e, name: 'SYSTEM_THREAD_EXCEPTION_NOT_HANDLED', drivers: ['portcls.sys'] },
  { code: 0x8e, name: 'KERNEL_MODE_EXCEPTION_NOT_HANDLED', drivers: ['win32k.sys'] },
  { code: 0xc2, name: 'BAD_POOL_CALLER', drivers: ['ks.sys'] },
  { code: 0xd1, name: 'DRIVER_IRQL_NOT_LESS_OR_EQUAL', drivers: ['nv4_disp.dll', 'ati2dvag.dll', 'gv3.sys'] },
  { code: 0xf4, name: 'CRITICAL_OBJECT_TERMINATION', drivers: [] },
  { code: 0x1a, name: 'MEMORY_MANAGEMENT', drivers: [] },
];

/** Uppercase, zero-padded, no `0x` — the way the technical block prints them. */
function hex(value: number, width: number): string {
  return Math.floor(value).toString(16).toUpperCase().padStart(width, '0');
}

function word(random: () => number): string {
  return hex(random() * 0xffffffff, 8);
}

/**
 * A kernel address: the 0x8xxxxxxx half of the address space, which is where
 * everything in the technical block actually lived.
 */
function kernelAddress(random: () => number): number {
  return 0x80000000 + Math.floor(random() * 0x7ff00000);
}

function pick<T>(items: readonly T[], random: () => number): T {
  return items[Math.min(items.length - 1, Math.floor(random() * items.length))];
}

/**
 * Roll one. `random` is injected rather than reaching for `Math.random`
 * directly so a test can pin the screen it gets.
 */
export function pickBugcheck(random: () => number = Math.random): Bugcheck {
  const check = pick(CHECKS, random);

  // The module is loaded on a page boundary and faults somewhere inside it,
  // which is the relationship anyone who has read one of these will expect
  // between the two addresses on the last line.
  //
  // Aligned by division rather than `& 0xfffff000`: every address here has the
  // top bit set, and JavaScript's bitwise operators would read that as a
  // negative int32 and hand back a base above the address it is meant to hold.
  const raw = kernelAddress(random);
  const base = raw - (raw % 0x1000);
  const address = base + Math.floor(random() * 0x9000);

  return {
    code: `0x${hex(check.code, 8)}`,
    name: check.name,
    args: [word(random), word(random), word(random), hex(address, 8)],
    driver:
      check.drivers.length === 0
        ? null
        : {
            name: pick(check.drivers, random),
            address: hex(address, 8),
            base: hex(base, 8),
            // A link timestamp from somewhere in XP's own lifetime, printed as
            // the raw `time_t` the loader stored.
            dateStamp: hex(0x3d000000 + Math.floor(random() * 0x08000000), 8).toLowerCase(),
          },
  };
}
